import React, { useState, useEffect } from "react";
import { auth } from "../firebase-config";
import { onAuthStateChanged } from "firebase/auth";
import robinhood from "../assets/images/robinhood.svg";
import schwab from "../assets/images/charles_schwab.svg";
import fidelity from "../assets/images/fidelity.svg";
import { RedditIcon, TwitterShareButton, XIcon } from "react-share";
import { logUserActivity } from "./logActivity";
import { FiLink, FiBell } from "react-icons/fi";
import axios from "axios";

const baseURL =
  process.env.REACT_APP_ENVIRONMENT_TYPE === "DEV"
    ? "http://127.0.0.1:5000"
    : "https://flask-backend-52245432644.us-central1.run.app";

const formatPredictionDate = (dateString) => {
  const date = new Date(dateString);
  const options = { year: "numeric", month: "long", day: "numeric" };
  return date.toLocaleDateString("en-US", options).split(",")[0];
};

const handleCopyLink = () => {
  const pid = (() => {
    const predictionData = localStorage.getItem("predictionData");
    if (predictionData) {
      try {
        return JSON.parse(predictionData)?.prediction_id || "unknown";
      } catch (error) {
        console.error("Error parsing predictionData from localStorage:", error);
        return "unknown";
      }
    } else {
      const currentUrl = window.location.href;
      return new URL(currentUrl).searchParams.get("pid") || "unknown";
    }
  })();

  const linkToCopy = `https://marketcrunch.ai/dashboard?pid=${pid}`;
  navigator.clipboard
    .writeText(linkToCopy)
    .then(() => {
      alert("Link copied to clipboard!");
      // Log the activity for page mapping as SH_L
      const userId =
        localStorage.getItem("uid") || localStorage.getItem("anonUserId");
      logUserActivity(userId, "SH_L");
    })
    .catch((error) =>
      console.error("Failed to copy link to clipboard:", error)
    );
};

const trackTradeNowClicks = ({ ticker, userId, platform }) => {
  if (typeof window.gtag !== "function") return;

  const timestamp = new Date().toISOString();

  window.gtag("event", "trade_now", {
    event_category: "button",
    value: 1,
    ticker,
    user_id: userId,
    platform,
    timestamp,
  });
};

const useWindowWidth = () => {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return width;
};

// Function to get the technical signal based on MACD and RSI values
const getTechnicalSignal = (macd, macdSignal, rsi) => {
  const macdNum = Number(macd);
  const macdSignalNum = Number(macdSignal);
  const rsiNum = Number(rsi);

  if (isNaN(macdNum) || isNaN(macdSignalNum) || isNaN(rsiNum)) {
    console.log("Invalid technical indicators:", { macd, macdSignal, rsi });
    return null;
  }

  if (macdNum >= macdSignalNum && rsiNum >= 70) {
    return "Bullish momentum with overbought conditions - watch for a potential pullback.";
  } else if (macdNum < macdSignalNum && rsiNum >= 70) {
    return "Bearish momentum with overbought conditions - potential for a pullback or deeper correction.";
  } else if (macdNum >= macdSignalNum && rsiNum < 30) {
    return "Bullish momentum with oversold conditions - potential for a strong rebound.";
  } else if (macdNum < macdSignalNum && rsiNum < 30) {
    return "Bearish momentum with oversold conditions - risk of further decline or bounce.";
  } else if (macdNum >= macdSignalNum && rsiNum >= 30 && rsiNum < 70) {
    return "Bullish momentum with stable conditions - trend continuation likely.";
  } else if (macdNum < macdSignalNum && rsiNum >= 30 && rsiNum < 70) {
    return "Bearish momentum with stable conditions - trend continuation likely.";
  }

  return null;
};

const PredictionCard = ({
  parsedPrediction,
  confidenceMeterMargin,
  accuracyMeterMargin,
}) => {
  const [user, setUser] = useState(null);
  const [anonUserId, setAnonUserId] = useState(
    localStorage.getItem("anonUserId") || null
  );
  const [isInFavorites, setIsInFavorites] = useState(false);
  const width = useWindowWidth();
  const sentimentLeftMap = {
    "very bullish": 90,
    bullish: 75,
    neutral: 50,
    bearish: 25,
    "very bearish": 10,
  };

  const addToFavorites = async () => {
    if (!parsedPrediction) return;
    const userId = localStorage.getItem("databaseUid");
    try {
      setIsInFavorites(true);
      await axios.get(
        `${baseURL}/add-to-favorites?uid=${userId}&ticker=${parsedPrediction.ticker.toUpperCase()}`
      );
    } catch (err) {
      console.log("Failed to add to favorites: " + err);
    }
  };

  const removeFromFavorites = async () => {
    if (!parsedPrediction) return;
    const userId = localStorage.getItem("databaseUid");
    try {
      setIsInFavorites(false);
      await axios.get(
        `${baseURL}/remove-from-favorites?uid=${userId}&ticker=${parsedPrediction.ticker.toUpperCase()}`
      );
    } catch (err) {
      console.log("Failed to remove from favorites: " + err);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUser(user);
      } else {
        setUser(null);
      }
    });
    // Fetch user's alerts (favorites) and set isInFavorites
    const checkIfInAlerts = async () => {
      if (!parsedPrediction || !parsedPrediction.ticker) return;
      const userId = localStorage.getItem("databaseUid");
      if (!userId) return;
      try {
        // Try to get cached data first
        const cachedData = localStorage.getItem(`favoritesData_${userId}`);
        const cachedTimestamp = localStorage.getItem(
          `favoritesDataTimestamp_${userId}`
        );
        const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
        let user_favorites = [];
        if (cachedData && cachedTimestamp) {
          const isExpired =
            Date.now() - parseInt(cachedTimestamp) > CACHE_DURATION;
          if (!isExpired) {
            const parsedData = JSON.parse(cachedData);
            if (parsedData && Array.isArray(parsedData.favorites)) {
              user_favorites = parsedData.favorites.map((entry) =>
                Array.isArray(entry) ? entry[0] : entry.ticker
              );
            }
          }
        }
        // If not found in cache, fetch from backend
        if (user_favorites.length === 0) {
          const response = await axios.get(`${baseURL}/get-favorites`, {
            params: { uid: userId },
            headers: { "Content-Type": "application/json" },
          });
          const fetchedData = response.data;
          if (fetchedData && Array.isArray(fetchedData.favorites)) {
            user_favorites = fetchedData.favorites.map((entry) =>
              Array.isArray(entry) ? entry[0] : entry.ticker
            );
          }
        }
        setIsInFavorites(
          user_favorites.includes(parsedPrediction.ticker.toUpperCase())
        );
      } catch (err) {
        // fallback to false
        setIsInFavorites(false);
      }
    };
    checkIfInAlerts();
    return () => unsubscribe();
  }, [user, parsedPrediction]);

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div className="add-to-fav">
          <div
            style={{
              display: "flex",
              flexDirection: "row",
            }}
          >
            <span
              style={{
                fontFamily: "Inter",
                fontSize: "16px",
                fontWeight: 500,
                color: "rgba(0, 0, 0, 0.75)",
              }}
            >
              {isInFavorites ? "Alert On:" : "Set Alert:"}
            </span>
            {parsedPrediction.ticker && (
              <button
                onClick={isInFavorites ? removeFromFavorites : addToFavorites}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  marginLeft: 5,
                }}
                aria-label={isInFavorites ? "Remove alert" : "Set alert"}
              >
                <FiBell
                  size={21}
                  style={{
                    // when ON, fill the bell; when OFF, keep it outline
                    fill: isInFavorites ? "rgba(248, 7, 7, 0.75)" : "none",
                    // stroke (outline) always uses currentColor
                    color: isInFavorites
                      ? "#FF4C61"
                      : "rgba(205, 77, 77, 0.75)",
                  }}
                />
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="stock-container">
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            width: "100%",
            padding: "20px",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div
              style={{
                display: "flex",
                flexDirection: window.innerWidth > 768 ? "row" : "column",
                alignItems: window.innerWidth > 768 ? "center" : "start",
                justifyContent: "center",
              }}
            >
              <h2
                style={{
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 700,
                  marginRight: 10,
                }}
              >
                {parsedPrediction.ticker.toUpperCase()}
              </h2>
              <span
                style={{
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: "Inter, sans-serif",
                  color: "rgba(255, 255, 255, 0.75)",
                }}
              >
                {parsedPrediction.company_info.longName}
              </span>
            </div>
            <span
              style={{
                textAlign: "left",
                fontSize: "14px",
                color: "rgba(255, 255, 255, 1)",
                fontFamily: "Inter, sans-serif",
                marginTop: 10,
                fontStyle: "italic",
              }}
            >
              {formatPredictionDate(parsedPrediction.pred_date)}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: 15,
              marginTop: 1.5,
            }}
          >
            <button
              style={{
                border: 0,
                backgroundColor: "transparent",
                borderRadius: 7,
                cursor: "pointer",
              }}
              onClick={() => {
                trackTradeNowClicks({
                  ticker: parsedPrediction.ticker.toUpperCase(),
                  userId: localStorage.getItem("databaseUid"),
                  platform: "robinhood",
                });
                window.open(
                  `https://robinhood.com/us/en/stocks/${parsedPrediction.ticker}/`,
                  "_blank"
                );
              }}
            >
              <img
                src={robinhood}
                alt="Trade on Robinhood"
                style={{ height: "25px", borderRadius: 50 }}
              />
            </button>
            <button
              style={{
                border: 0,
                backgroundColor: "transparent",
                cursor: "pointer",
              }}
              onClick={() => {
                trackTradeNowClicks({
                  ticker: parsedPrediction.ticker.toUpperCase(),
                  userId: localStorage.getItem("databaseUid"),
                  platform: "fidelity",
                });
                window.open(
                  `https://digital.fidelity.com/prgw/digital/research/quote/dashboard/summary?symbol=${parsedPrediction.ticker}`,
                  "_blank"
                );
              }}
            >
              <img
                src={fidelity}
                alt="Trade on Fidelity"
                style={{ height: "25px", borderRadius: 50 }}
              />
            </button>
            <button
              style={{
                border: 0,
                backgroundColor: "transparent",
                borderRadius: 7,
                cursor: "pointer",
              }}
              onClick={() => {
                trackTradeNowClicks({
                  ticker: parsedPrediction.ticker.toUpperCase(),
                  userId: localStorage.getItem("databaseUid"),
                  platform: "schwab",
                });
                window.open(
                  `https://www.schwab.com/research/stocks/quotes/summary/${parsedPrediction.ticker}`,
                  "_blank"
                );
              }}
            >
              <img
                src={schwab}
                alt="Trade on Schwab"
                style={{ height: "25px", borderRadius: 50 }}
              />
            </button>
          </div>
        </div>
        <div className="title-container">
          {/* Prediction + Meter Column */}
          <div
            style={{
              display: "flex",
              alignItems: "start",
              flexDirection: "column",
              marginRight: window.innerWidth > 768 ? 10 : 0,
              width: window.innerWidth > 768 ? "50%" : "100%",
              marginBottom: window.innerWidth > 768 ? 0 : 20,
            }}
          >
            <p
              style={{
                textAlign: "start",
                fontFamily: "Inter, sans-serif",
                fontWeight: 500,
              }}
            >
              {Math.abs(parseFloat(parsedPrediction.next_day_change_percent)) >=
              0.02 ? (
                <span
                  className={
                    parseFloat(parsedPrediction.next_day_change_percent) > 0
                      ? "positive-change"
                      : "negative-change"
                  }
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    marginRight: "8px",
                  }}
                >
                  <i
                    className={`fas fa-arrow-trend-${
                      parseFloat(parsedPrediction.next_day_change_percent) > 0
                        ? "up"
                        : "down"
                    }`}
                  ></i>
                </span>
              ) : null}
              <span>
                {"Prediction: "}
                {parsedPrediction.price_target}{" "}
                {Math.abs(
                  parseFloat(parsedPrediction.next_day_change_percent)
                ) < 0.02 ? (
                  "(flat ±0.02%)"
                ) : (
                  <span
                    className={
                      parseFloat(parsedPrediction.next_day_change_percent) > 0
                        ? "positive-change"
                        : "negative-change"
                    }
                  >
                    ({parsedPrediction.next_day_change_percent})
                  </span>
                )}
                <br />
              </span>
            </p>
            {(parsedPrediction.confidence_level ||
              parsedPrediction.news_sentiment_label ||
              (parsedPrediction.accuracy !== undefined &&
                parsedPrediction.accuracy.toString() !== "0")) && (
              <div className="metrics-container">
                <div className="metric-item">
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      Accuracy (last 90d)
                    </span>
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      {parsedPrediction.accuracy === -1 ||
                      parsedPrediction.accuracy < 0
                        ? "N/A"
                        : `${Math.min(
                            Math.max(parsedPrediction.accuracy * 100, 0),
                            100
                          ).toFixed(0)}%`}
                    </span>
                  </div>
                  <div
                    className="confidence-level"
                    style={{
                      "--mask-position": `${accuracyMeterMargin}%`,
                    }}
                  >
                    <div
                      className="confidence-line"
                      style={{
                        left: `${accuracyMeterMargin}%`,
                      }}
                    />
                  </div>
                </div>
                <div className="metric-item">
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      AI Model Confidence
                    </span>
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      {`${confidenceMeterMargin.toFixed(0)}%`}
                    </span>
                  </div>
                  <div
                    className="confidence-level"
                    style={{
                      "--mask-position": `${confidenceMeterMargin}%`,
                    }}
                  >
                    <div
                      className="confidence-line"
                      style={{
                        left: `${confidenceMeterMargin}%`,
                      }}
                    />
                  </div>
                </div>
                <div className="metric-item">
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      News Sentiment
                    </span>
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      {parsedPrediction.news_sentiment_label}
                    </span>
                  </div>
                  <div
                    className="confidence-level"
                    style={{
                      "--mask-position": `${
                        sentimentLeftMap[
                          parsedPrediction.news_sentiment_label.toLowerCase()
                        ] || 50
                      }%`,
                    }}
                  >
                    <div
                      className="confidence-line"
                      style={{
                        left: `${
                          sentimentLeftMap[
                            parsedPrediction.news_sentiment_label.toLowerCase()
                          ] || 50
                        }%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Technical + Analyst Column */}
          <div
            style={{
              marginLeft: window.innerWidth > 768 ? 10 : 0,
              width: window.innerWidth > 768 ? "50%" : "100%",
            }}
          >
            {(parsedPrediction.MACD ||
              parsedPrediction.MACD_Signal ||
              parsedPrediction.RSI_14) && (
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "row",
                  }}
                >
                  <i
                    className="fa-solid fa-magnifying-glass-chart"
                    style={{ color: "cornflowerblue" }}
                  ></i>
                  <span
                    style={{
                      fontSize: 18,
                      fontWeight: "bold",
                      fontFamily: "Inter, sans-serif",
                      alignSelf: "start",
                      display: "flex",
                      marginLeft: 10,
                      color: "#1d3557",
                    }}
                  >
                    Technical Analysis
                  </span>
                </div>
                {(() => {
                  // Add detailed logging for debugging
                  // console.log(
                  //   "Checking technical indicators in parsedPrediction:",
                  //   {
                  //     original_MACD: parsedPrediction.MACD,
                  //     original_MACD_Signal: parsedPrediction.MACD_Signal,
                  //     original_RSI_14: parsedPrediction.RSI_14,
                  //     lowercase_macd: parsedPrediction.macd,
                  //     lowercase_macd_signal: parsedPrediction.macd_signal,
                  //     lowercase_rsi_14: parsedPrediction.rsi_14,
                  //   }
                  // );

                  // Function to round values to 3 significant digits
                  const roundToSig = (num) => {
                    if (num === undefined || num === null || isNaN(Number(num)))
                      return "N/A";
                    const numValue = Number(num);
                    if (Math.abs(numValue) < 10) {
                      return Number(numValue.toFixed(2));
                    } else if (Math.abs(numValue) < 100) {
                      return Number(numValue.toFixed(1));
                    } else {
                      return Math.round(numValue);
                    }
                  };

                  // Get MACD, MACD_Signal, RSI values from either lowercase or uppercase properties
                  let macdValue, macdSignalValue, rsiValue;
                  let signalMessage = null;

                  // Try lowercase properties first
                  if (
                    parsedPrediction.macd !== undefined &&
                    parsedPrediction.macd_signal !== undefined &&
                    parsedPrediction.rsi_14 !== undefined
                  ) {
                    macdValue = Number(parsedPrediction.macd);
                    macdSignalValue = Number(parsedPrediction.macd_signal);
                    rsiValue = Number(parsedPrediction.rsi_14);

                    if (
                      !isNaN(macdValue) &&
                      !isNaN(macdSignalValue) &&
                      !isNaN(rsiValue)
                    ) {
                      signalMessage = getTechnicalSignal(
                        macdValue,
                        macdSignalValue,
                        rsiValue
                      );
                    }
                  }

                  // If no valid lowercase properties, try uppercase
                  if (
                    signalMessage === null &&
                    parsedPrediction.MACD !== undefined &&
                    parsedPrediction.MACD_Signal !== undefined &&
                    parsedPrediction.RSI_14 !== undefined
                  ) {
                    macdValue = Number(parsedPrediction.MACD);
                    macdSignalValue = Number(parsedPrediction.MACD_Signal);
                    rsiValue = Number(parsedPrediction.RSI_14);

                    if (
                      !isNaN(macdValue) &&
                      !isNaN(macdSignalValue) &&
                      !isNaN(rsiValue)
                    ) {
                      signalMessage = getTechnicalSignal(
                        macdValue,
                        macdSignalValue,
                        rsiValue
                      );
                    }
                  }

                  // If we have a signal message and valid values, display them
                  if (
                    signalMessage &&
                    macdValue !== undefined &&
                    !isNaN(macdValue) &&
                    macdSignalValue !== undefined &&
                    !isNaN(macdSignalValue) &&
                    rsiValue !== undefined &&
                    !isNaN(rsiValue)
                  ) {
                    const roundedMacd = roundToSig(macdValue);
                    const roundedMacdSignal = roundToSig(macdSignalValue);
                    const roundedRsi = roundToSig(rsiValue);

                    // Use window width to determine layout
                    const isMobile = width < 675;

                    return (
                      <div
                        className="technical-signal"
                        style={{
                          marginTop: "8px",
                          fontSize: "14px",
                          fontFamily: "Merriweather, serif",
                          color: "#1D3557",
                          fontWeight: "normal",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <span
                            style={{
                              fontWeight: "500",
                              textAlign: "start",
                              marginBottom: 10,
                              color: "rgba(0, 0, 0, 0.75)",
                              fontFamily: "Inter, sans-serif",
                            }}
                          >
                            {signalMessage}
                          </span>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(3, 1fr)",
                              fontSize: "14px",
                              color: "black",
                              gap: 10,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "start",
                              }}
                            >
                              <span
                                style={{
                                  fontFamily: "Inter, sans-serif",
                                  fontSize: 14,
                                  fontWeight: "bold",
                                  fontStyle: "italic",
                                  color: "black",
                                }}
                              >
                                MACD
                              </span>
                              <span
                                style={{
                                  fontWeight: "400",
                                  fontSize: "13px",
                                  color: "rgba(0, 0, 0, 0.75)",
                                  fontFamily: "Inter, sans-serif",
                                  marginBottom: 5,
                                }}
                              >
                                {roundedMacd}
                              </span>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "start",
                              }}
                            >
                              <span
                                style={{
                                  fontFamily: "Inter, sans-serif",
                                  fontSize: 14,
                                  fontWeight: "bold",
                                  fontStyle: "italic",
                                  color: "black",
                                }}
                              >
                                Signal
                              </span>
                              <span
                                style={{
                                  fontWeight: "400",
                                  fontSize: "13px",
                                  color: "rgba(0, 0, 0, 0.75)",
                                  fontFamily: "Inter, sans-serif",
                                  marginBottom: 5,
                                }}
                              >
                                {roundedMacdSignal}
                              </span>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "start",
                              }}
                            >
                              <span
                                style={{
                                  fontFamily: "Inter, sans-serif",
                                  fontSize: 15,
                                  fontWeight: "bold",
                                  fontStyle: "italic",
                                  color: "black",
                                }}
                              >
                                RSI
                              </span>
                              <span
                                style={{
                                  fontWeight: "400",
                                  fontSize: "13px",
                                  color: "rgba(0, 0, 0, 0.75)",
                                  fontFamily: "Inter, sans-serif",
                                  marginBottom: 5,
                                }}
                              >
                                {roundedRsi}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return null;
                })()}
              </div>
            )}
            {parsedPrediction.analyst_data && (
              <div style={{ marginTop: 20 }}>
                {(parsedPrediction.analyst_data.min_price_target ||
                  parsedPrediction.analyst_data.avg_price_target ||
                  parsedPrediction.analyst_data.max_price_target ||
                  parsedPrediction.analyst_data.analyst_recommendation) && (
                  <>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "row",
                      }}
                    >
                      <i
                        className="fa-solid fa-chart-bar"
                        style={{ color: "darkseagreen" }}
                      ></i>
                      <span
                        style={{
                          fontSize: 18,
                          fontWeight: "bold",
                          fontFamily: "Inter, sans-serif",
                          alignSelf: "start",
                          display: "flex",
                          marginLeft: 10,
                          color: "#1d3557",
                        }}
                      >
                        Analyst Forecast (12 mo.)
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        marginTop: 10,
                      }}
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "0.9fr 0.9fr 0.9fr 1.3fr",
                        }}
                      >
                        {parsedPrediction.analyst_data.min_price_target && (
                          <span
                            style={{
                              fontFamily: "Inter, sans-serif",
                              fontSize: 14,
                              fontStyle: "italic",
                              textAlign: "left",
                              lineHeight: "1.33em",
                              minHeight: "calc(2*1.33em)",
                              boxOrient: "vertical",
                              display: "inline-block",
                              overflow: "hidden",
                              width: "100%",
                              marginBottom: 5,
                            }}
                          >
                            Min
                          </span>
                        )}
                        {parsedPrediction.analyst_data.avg_price_target && (
                          <span
                            style={{
                              fontFamily: "Inter, sans-serif",
                              fontSize: 14,
                              fontStyle: "italic",
                              textAlign: "left",
                              lineHeight: "1.33em",
                              minHeight: "calc(2*1.33em)",
                              boxOrient: "vertical",
                              display: "inline-block",
                              overflow: "hidden",
                              width: "100%",
                              marginBottom: 5,
                            }}
                          >
                            Avg
                          </span>
                        )}
                        {parsedPrediction.analyst_data.max_price_target && (
                          <span
                            style={{
                              fontFamily: "Inter, sans-serif",
                              fontSize: 14,
                              fontStyle: "italic",
                              textAlign: "left",
                              lineHeight: "1.33em",
                              minHeight: "calc(2*1.33em)",
                              boxOrient: "vertical",
                              display: "inline-block",
                              overflow: "hidden",
                              width: "100%",
                              marginBottom: 5,
                            }}
                          >
                            Max
                          </span>
                        )}
                        {parsedPrediction.analyst_data
                          .analyst_recommendation && (
                          <span
                            style={{
                              fontFamily: "Inter, sans-serif",
                              fontSize: 14,
                              fontStyle: "italic",
                              textAlign: "left",
                              lineHeight: "1.33em",
                              minHeight: "calc(2*1.33em)",
                              boxOrient: "vertical",
                              display: "inline-block",
                              overflow: "hidden",
                              width: "100%",
                              marginBottom: 5,
                            }}
                          >
                            Analysts Recommend
                          </span>
                        )}
                      </div>
                      <hr style={{ border: "1px solid gray" }} />
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "0.9fr 0.9fr 0.9fr 1.3fr",
                          marginTop: 10,
                        }}
                      >
                        {parsedPrediction.analyst_data.min_price_target && (
                          <span
                            style={{
                              fontWeight: "400",
                              fontSize: "13px",
                              color: "rgba(0, 0, 0, 0.75)",
                              fontFamily: "Inter, sans-serif",
                              marginBottom: 5,
                              textAlign: "left",
                            }}
                          >
                            $
                            {parsedPrediction.analyst_data.min_price_target.toFixed(
                              0
                            )}
                          </span>
                        )}
                        {parsedPrediction.analyst_data.avg_price_target && (
                          <span
                            style={{
                              fontWeight: "400",
                              fontSize: "13px",
                              color: "rgba(0, 0, 0, 0.75)",
                              fontFamily: "Inter, sans-serif",
                              marginBottom: 5,
                              textAlign: "left",
                            }}
                          >
                            $
                            {parsedPrediction.analyst_data.avg_price_target.toFixed(
                              0
                            )}
                          </span>
                        )}
                        {parsedPrediction.analyst_data.max_price_target && (
                          <span
                            style={{
                              fontWeight: "400",
                              fontSize: "13px",
                              color: "rgba(0, 0, 0, 0.75)",
                              fontFamily: "Inter, sans-serif",
                              marginBottom: 5,
                              textAlign: "left",
                            }}
                          >
                            $
                            {parsedPrediction.analyst_data.max_price_target.toFixed(
                              0
                            )}
                          </span>
                        )}
                        {parsedPrediction.analyst_data
                          .analyst_recommendation && (
                          <span
                            style={{
                              fontWeight: "400",
                              fontSize: "13px",
                              color: "rgba(0, 0, 0, 0.75)",
                              fontFamily: "Inter, sans-serif",
                              marginBottom: 5,
                              textAlign: "left",
                            }}
                          >
                            {parsedPrediction.analyst_data.analyst_recommendation
                              .toUpperCase()
                              .replace("_", " ")}
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="reddit-share-container">
        <div style={{ display: "flex", flexDirection: "row" }}>
          <span
            style={{
              fontFamily: "Inter",
              fontSize: "16px",
              fontWeight: 500,
              color: "rgba(0, 0, 0, 0.75)",
            }}
          >
            Share:
          </span>
          <span
            className="copy-link-icon"
            onClick={handleCopyLink}
            onMouseEnter={(e) => {
              const tooltipshare = document.createElement("div");
              tooltipshare.textContent = "Copy Link";
              tooltipshare.className = "tooltip";
              tooltipshare.style.position = "absolute";
              tooltipshare.style.backgroundColor = "#333";
              tooltipshare.style.color = "#fff";
              tooltipshare.style.padding = "4px 8px";
              tooltipshare.style.borderRadius = "4px";
              tooltipshare.style.fontSize = "12px";
              tooltipshare.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.2)";
              tooltipshare.style.whiteSpace = "nowrap";
              tooltipshare.style.pointerEvents = "none";
              tooltipshare.style.zIndex = "1000";
              tooltipshare.style.top = `${e.clientY - 60}px`;
              tooltipshare.style.left = `${e.clientX - 45}px`;
              document.body.appendChild(tooltipshare);
              e.currentTarget.tooltipshare = tooltipshare;
            }}
            onMouseLeave={(e) => {
              if (e.currentTarget.tooltipshare) {
                document.body.removeChild(e.currentTarget.tooltipshare);
                e.currentTarget.tooltipshare = null;
              }
            }}
            style={{
              cursor: "pointer",
              margin: "0 8px",
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            <FiLink size={16} />
          </span>

          <a
            href={`https://www.reddit.com/submit?url=${encodeURIComponent(
              `https://marketcrunch.ai/dashboard?pid=${(() => {
                let predictionId = localStorage.getItem("predictionId");
                if (predictionId) {
                  try {
                    return predictionId || "unknown";
                  } catch (error) {
                    console.error(
                      "Error parsing predictionData from localStorage:",
                      error
                    );
                    return "unknown";
                  }
                } else {
                  // Use the current page URL as a fallback
                  const currentUrl = window.location.href;
                  return (
                    new URL(currentUrl).searchParams.get("pid") || "unknown"
                  );
                }
              })()}`
            )}&title=${encodeURIComponent(
              `${parsedPrediction.ticker} | MarketCrunch's deep-learning model analyzed ${parsedPrediction.data_points} trading data points since ${parsedPrediction.first_trade_date}. See price prediction:`
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            onMouseEnter={(e) => {
              const tooltipshare = document.createElement("div");
              tooltipshare.textContent = "Share to Reddit";
              tooltipshare.className = "tooltip";
              tooltipshare.style.position = "absolute";
              tooltipshare.style.backgroundColor = "#333";
              tooltipshare.style.color = "#fff";
              tooltipshare.style.padding = "4px 8px";
              tooltipshare.style.borderRadius = "4px";
              tooltipshare.style.fontSize = "12px";
              tooltipshare.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.2)";
              tooltipshare.style.whiteSpace = "nowrap";
              tooltipshare.style.pointerEvents = "none";
              tooltipshare.style.zIndex = "1000";
              tooltipshare.style.top = `${e.clientY - 60}px`;
              tooltipshare.style.left = `${e.clientX - 50}px`;
              document.body.appendChild(tooltipshare);
              e.currentTarget.tooltipshare = tooltipshare;
            }}
            onMouseLeave={(e) => {
              if (e.currentTarget.tooltipshare) {
                document.body.removeChild(e.currentTarget.tooltipshare);
                e.currentTarget.tooltipshare = null;
              }
            }}
            className="reddit-share-button"
            onClick={(e) => {
              // Log the activity for page mapping as SH_R
              const userId = localStorage.getItem("uid") || anonUserId;
              logUserActivity(userId, "SH_R");
              // Prevent default if you need to handle the click differently,
              // but here we want the href to navigate, so no e.preventDefault()
            }}
          >
            <RedditIcon size={24} round={true} />
          </a>
          <TwitterShareButton
            onMouseEnter={(e) => {
              const tooltipshare = document.createElement("div");
              tooltipshare.textContent = "Share to Twitter";
              tooltipshare.className = "tooltip";
              tooltipshare.style.position = "absolute";
              tooltipshare.style.backgroundColor = "#333";
              tooltipshare.style.color = "#fff";
              tooltipshare.style.padding = "4px 8px";
              tooltipshare.style.borderRadius = "4px";
              tooltipshare.style.fontSize = "12px";
              tooltipshare.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.2)";
              tooltipshare.style.whiteSpace = "nowrap";
              tooltipshare.style.pointerEvents = "none";
              tooltipshare.style.zIndex = "1000";
              tooltipshare.style.top = `${e.clientY - 60}px`;
              tooltipshare.style.left = `${e.clientX - 80}px`;
              document.body.appendChild(tooltipshare);
              e.currentTarget.tooltipshare = tooltipshare;
            }}
            onMouseLeave={(e) => {
              if (e.currentTarget.tooltipshare) {
                document.body.removeChild(e.currentTarget.tooltipshare);
                e.currentTarget.tooltipshare = null;
              }
            }}
            style={{ marginLeft: 10 }}
            url={`https://marketcrunch.ai/dashboard?pid=${(() => {
              const predictionData = localStorage.getItem("predictionData");
              if (predictionData) {
                try {
                  return JSON.parse(predictionData)?.prediction_id || "unknown";
                } catch (error) {
                  console.error(
                    "Error parsing predictionData from localStorage:",
                    error
                  );
                  return "unknown";
                }
              } else {
                // Use the current page URL as a fallback
                const currentUrl = window.location.href;
                return new URL(currentUrl).searchParams.get("pid") || "unknown";
              }
            })()}`}
            title={`${parsedPrediction.ticker} | MarketCrunch's deep-learning model analyzed ${parsedPrediction.data_points} trading data points since ${parsedPrediction.first_trade_date}. See price prediction:`}
            onClick={() => {
              // Log the activity for page mapping as SH_R
              const userId = localStorage.getItem("uid") || anonUserId;
              logUserActivity(userId, "SH_X");
            }}
          >
            <XIcon size={24} round={true} />
          </TwitterShareButton>
        </div>
      </div>
    </>
  );
};

export default PredictionCard;
